import React, { createContext, FC, useCallback, useContext, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { mediaManagerStorageStatusOpenMm } from '@wix/bi-logger-media-data/v2';
import { usePrevious } from 'react-use';
import { BIPageName } from '../../../../constants/Bi';
import { DuplexerEvent } from '../../../../constants/DuplexerEvent';
import { storagePlanUpgradedNotification } from '../../../../containers/FilesManager/notifications/storagePlanUpgradedNotification';
import { EVENT_INSTANCE_EXPIRED, EVENT_STORAGE_QUOTA_SERVER_ERROR, useAppEvents } from '../../../../services/appEvents';
import { bi } from '../../../../services/loggers/bi';
import { parentConnection as parentConnectionDefault } from '../../../../services/parentConnection';
import { useMMGRSettings } from '../../../hoc/withMMGRSettings';
import { parseSiteMediaToken } from '../../../siteMediaToken';
import { TDuplexerHandler, useDuplexer } from '../../useDuplexer';
import { useFormatBytes } from '../../useFormattedBytes';
import { useNotificatorConsumer } from '../../useNotificator';
import { calculateBiStatus } from '../useStorageQuotaBI';
import { enrichStorageQuotaData } from './enrichStorageQuotaData';
import { fetchQuotaData as fetchQuotaDataDefault } from './fetchQuotaData';
import { EventEmitterOnOff, IStorageQuotaData, IStorageQuotaDataBase } from './types';
import { useStorageQuotaExperiment } from './useStorageQuotaExperiment';
import { CustomMonitoringTag, FedOpsInteraction } from '../../../../constants/FedOpsInteraction';

export const StorageQuotaContext = createContext<{
  storageQuotaData?: IStorageQuotaData;
  isLoading: boolean;
  isStorageQuotaVisibilityEnabled: boolean;
  isStorageQuotaEnforcementEnabled: boolean;
  isStorageQuotaGracePeriodEnabled: boolean;
}>({
  storageQuotaData: undefined,
  isLoading: false,
  isStorageQuotaVisibilityEnabled: false,
  isStorageQuotaEnforcementEnabled: false,
  isStorageQuotaGracePeriodEnabled: false,
});

interface IStorageQuotaProvider {
  fetchQuotaData?: (metaSiteToken: string) => Promise<IStorageQuotaDataBase>;
  parentConnection?: EventEmitterOnOff;
}

const fireStorageStatusBI = (freshStorageQuotaData: IStorageQuotaData) => {
  if (!freshStorageQuotaData) {
    captureException(new Error(`[storage quota] No storage data returned from the request`));
    return;
  }

  const { usage, quota } = freshStorageQuotaData;
  const storageStatus = calculateBiStatus(freshStorageQuotaData);
  bi.logger().report(
    mediaManagerStorageStatusOpenMm({
      used_storage: usage.storage,
      storage_limit: quota.storage,
      storage_status: storageStatus,
      pageName: BIPageName.MediaManager,
    }),
  );
};

export const StorageQuotaProvider: FC<IStorageQuotaProvider> = ({
  children,
  fetchQuotaData = fetchQuotaDataDefault,
  parentConnection = parentConnectionDefault,
}) => {
  const { siteMediaToken, metaSiteToken } = useMMGRSettings().mmgrSettings;
  const {
    isStorageQuotaEnforcementEnabled,
    isStorageQuotaGracePeriodEnabled,
    isStorageQuotaVisibilityEnabled,
    gracePeriodDaysLeft,
  } = useStorageQuotaExperiment();

  const [storageQuotaData, setStorageQuotaData] = useState<IStorageQuotaData>();
  const updateStorageQuotaData = useCallback(
    (freshStorageQuotaData: IStorageQuotaDataBase) => {
      const result = enrichStorageQuotaData({
        storageQuotaDataBase: freshStorageQuotaData,
        isStorageQuotaGracePeriodEnabled,
        isStorageQuotaEnforcementEnabled,
        gracePeriodDaysLeft,
      });
      setStorageQuotaData(result);
      return result;
    },
    [setStorageQuotaData, isStorageQuotaEnforcementEnabled, isStorageQuotaGracePeriodEnabled, gracePeriodDaysLeft],
  );
  const [isLoading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const { appEvents } = useAppEvents();

  const downloadFreshData = useCallback(async () => {
    if (!metaSiteToken) {
      return;
    }

    setLoading(true);
    setIsError(false);
    try {
      return updateStorageQuotaData(await fetchQuotaData(metaSiteToken));
    } catch (error) {
      if (error && error.message && error.message.includes('401')) {
        appEvents.emit(EVENT_INSTANCE_EXPIRED);
      } else {
        captureException(
          new Error(`[storage quota] downloadFreshData: ${error.message}`, {
            cause: error,
          }),
          {
            tags: {
              [CustomMonitoringTag]: FedOpsInteraction.StorageQuotaDataLoad,
            },
          },
        );
        setIsError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchQuotaData, metaSiteToken, updateStorageQuotaData, setLoading]);

  useEffect(() => {
    if (metaSiteToken && !storageQuotaData && !isError) {
      downloadFreshData().then(fireStorageStatusBI);
    }
  }, [metaSiteToken, storageQuotaData, downloadFreshData, isError]);

  useEffect(() => {
    const refreshOnReopen = () => downloadFreshData().then(fireStorageStatusBI);

    parentConnection.on('settings', refreshOnReopen);
    appEvents.on(EVENT_STORAGE_QUOTA_SERVER_ERROR, downloadFreshData);
    return () => {
      parentConnection.off('settings', refreshOnReopen);
      appEvents.off(EVENT_STORAGE_QUOTA_SERVER_ERROR, downloadFreshData);
    };
  }, [appEvents, downloadFreshData, parentConnection]);

  const previousQuota = usePrevious(storageQuotaData?.quota?.storage);
  const { notify } = useNotificatorConsumer();
  const formatBytes = useFormatBytes();

  useEffect(() => {
    if (!isStorageQuotaVisibilityEnabled) {
      return;
    }

    const currentUsage = storageQuotaData?.usage?.storage;
    const currentQuota = storageQuotaData?.quota?.storage;
    const spaceAvailable = currentQuota - currentUsage;

    if (!currentQuota || !previousQuota || !currentUsage || spaceAvailable < 0) {
      return;
    }

    if (currentQuota > previousQuota) {
      const isUpgradeAvailable = !!storageQuotaData?.quota?.plans?.upgradeUrl;
      notify(storagePlanUpgradedNotification(formatBytes(spaceAvailable), isUpgradeAvailable), {
        timeout: 30000,
      });
    }
  }, [isStorageQuotaVisibilityEnabled, storageQuotaData, previousQuota, notify, formatBytes]);

  useStorageUsageUpdateMessages((message) => {
    const { storage: usage, lastProcessed } = message.info.data.total.usage;
    const lastProcessedDateInUpdate = new Date(lastProcessed);
    const lastProcessedDateInState = storageQuotaData?.usage?.lastProcessed;
    const isOldUpdate = !!lastProcessedDateInState && lastProcessedDateInState > lastProcessedDateInUpdate;

    if (!storageQuotaData || isOldUpdate) {
      return;
    }

    updateStorageQuotaData({
      ...storageQuotaData,
      usage: {
        ...storageQuotaData.usage,
        storage: usage,
        lastProcessed: lastProcessedDateInUpdate,
      },
    });
  }, isStorageQuotaVisibilityEnabled);

  const { metaSiteId } = parseSiteMediaToken(siteMediaToken);

  useDuplexer({
    [DuplexerEvent.PremiumPlanChanged]: (message) => {
      if (!isStorageQuotaVisibilityEnabled) {
        return;
      }
      if (message.info.data.siteId === metaSiteId) {
        downloadFreshData();
      }
    },
  });

  return (
    <StorageQuotaContext.Provider
      value={{
        storageQuotaData,
        isLoading,
        isStorageQuotaVisibilityEnabled,
        isStorageQuotaEnforcementEnabled,
        isStorageQuotaGracePeriodEnabled,
      }}
    >
      {children}
    </StorageQuotaContext.Provider>
  );
};

export const useStorageQuota = () => useContext(StorageQuotaContext);

export const useStorageUsageUpdateMessages = (handler: TDuplexerHandler, isStorageQuotaVisibilityEnabled: boolean) => {
  const { metaSiteId } = parseSiteMediaToken(useMMGRSettings().mmgrSettings.siteMediaToken);

  useDuplexer({
    [DuplexerEvent.StorageUsageUpdate]: (message) => {
      if (!isStorageQuotaVisibilityEnabled) {
        return;
      }

      const metaSiteIdFromMessage = message.info.data.total.id;
      if (metaSiteId === metaSiteIdFromMessage) {
        handler(message);
      }
    },
  });
};
